import { makeAutoObservable } from "mobx";

import { IntervalAttributes, IntervalFragment } from "@/gql/graphql";
import { formatISOTimeOfDay, parseTimeOfDay } from "@/utils/datetime";
import { apply } from "@/utils/functions";

let intervalKeySeq = 0;

export default class Interval {
  id?: string;
  // Stable client-side key so React can track a not-yet-persisted interval
  // across re-sorts (presentIntervals sorts by start time as blocks move).
  key = `interval-${(intervalKeySeq += 1)}`;
  startTime?: Date | null | undefined = null;
  endTime?: Date | null | undefined = null;
  _destroy?: boolean;
  // Set by the owning Rule's overlap check (a cross-interval concern), so the
  // per-interval error message stays a single derived value instead of two
  // autoruns racing to write the same field.
  overlapping = false;

  // Complete and correctly ordered and not overlapping. Drives save-blocking
  // (isFormValid). A half-filled interval is "not valid" so save stays
  // disabled, but it is not surfaced as an error until the coach has entered
  // both times (see errorMessage).
  get isValid(): boolean {
    if (!this.startTime || !this.endTime) return false;
    if (new Date(this.endTime) <= new Date(this.startTime)) return false;
    return !this.overlapping;
  }

  // User-facing validation text. Empty while the interval is still being
  // filled in, so adding a new row doesn't immediately flash an error before
  // the coach has picked any times.
  get errorMessage(): string | undefined {
    if (this._destroy) return undefined;
    if (!this.startTime || !this.endTime) return undefined;
    if (new Date(this.endTime) <= new Date(this.startTime))
      return "end time must be after start time";
    if (this.overlapping) return "this interval overlaps with another interval";
    return undefined;
  }

  get interval(): { start: Date; end: Date } | undefined {
    if (this.startTime && this.endTime) {
      return { start: this.startTime, end: this.endTime };
    }
    return undefined;
  }

  constructor() {
    makeAutoObservable(this);
  }

  setIntervalStartTime = (date: Date | null) => {
    this.startTime = date;
  };

  setIntervalEndTime = (date: Date | null) => {
    this.endTime = date;
  };

  toAttributes = (): IntervalAttributes => {
    return {
      id: this.id,
      startTime: formatISOTimeOfDay(this.startTime ?? new Date()),
      endTime: formatISOTimeOfDay(this.endTime ?? new Date()),
      _destroy: this._destroy,
    };
  };

  restore = () => {
    this._destroy = false;
  };

  static fromFragment(fragment: IntervalFragment) {
    const newInterval = new Interval();
    newInterval.startTime = apply(fragment.startTime, parseTimeOfDay);
    newInterval.endTime = apply(fragment.endTime, parseTimeOfDay);
    newInterval.id = fragment.id;

    return newInterval;
  }
}
